import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../api/config';

interface Flow {
  _id: string;
  flowName: string;
  llmProvider?: 'openai' | 'gemini';
  openAIModels?: {
    chat: string;
    image: string;
  };
  geminiModels?: {
    chat: string;
    image: string;
  };
  minQuestions: number;
  maxQuestions: number;
  contextPrompt: string;
  styleConfig: {
    roundedCorners: boolean;
    fontType: string;
    fontSize: string;
    includeImages: boolean;
    baseColors: string[];
  };
  finalScreen: {
    title: string;
    message: string;
    ctaText: string;
    ctaLink: string;
  };
}

const FlowPreview: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [flow, setFlow] = useState<Flow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchFlow();
  }, [id]);

  const fetchFlow = async () => {
    try {
      const response = await api.get(`/flows/${id}`);
      setFlow(response.data);
    } catch (err) {
      setError('Failed to fetch flow');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !flow) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Flow not found'}</p>
          <button
            onClick={() => navigate('/admin')}
            className="text-blue-600 hover:text-blue-800"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const { styleConfig } = flow;
  const provider = flow.llmProvider || 'openai';
  const models =
    provider === 'gemini' ? flow.geminiModels : flow.openAIModels;
  const fontSize = {
    small: 'text-sm',
    medium: 'text-base',
    large: 'text-lg'
  }[styleConfig.fontSize];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <h1 className="text-2xl font-bold text-gray-900">
              Preview: {flow.flowName}
            </h1>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(`/admin/flows/${id}/edit`)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Edit Flow
              </button>
              <button
                onClick={() => navigate('/admin')}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Preview Content */}
      <main className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white shadow rounded-lg p-6"
        >
          <h2 className="text-xl font-semibold mb-6">Flow Configuration</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-medium text-gray-700">URL</h3>
              <p className="text-gray-600">/{flow.flowName}</p>
            </div>

            <div>
              <h3 className="font-medium text-gray-700">AI Questions Range</h3>
              <p className="text-gray-600">{flow.minQuestions} - {flow.maxQuestions} AI-generated questions, plus mandatory questions</p>
            </div>

            <div>
              <h3 className="font-medium text-gray-700">Context Prompt</h3>
              <p className="text-gray-600 whitespace-pre-wrap">{flow.contextPrompt}</p>
            </div>

            <div>
              <h3 className="font-medium text-gray-700 mb-2">
                AI Configuration
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <p className="text-sm">
                  <span className="font-medium">Provider:</span>{' '}
                  {provider === 'gemini' ? 'Gemini' : 'OpenAI'}
                </p>
                <p className="text-sm">
                  <span className="font-medium">Chat Model:</span>{' '}
                  {models?.chat || 'Default'}
                </p>
                {styleConfig.includeImages && (
                  <p className="text-sm">
                    <span className="font-medium">Image Model:</span>{' '}
                    {models?.image || 'Default'}
                  </p>
                )}
              </div>
            </div>

            <div>
              <h3 className="font-medium text-gray-700 mb-2">Style Configuration</h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <p className="text-sm"><span className="font-medium">Font:</span> {styleConfig.fontType}</p>
                <p className="text-sm"><span className="font-medium">Size:</span> {styleConfig.fontSize}</p>
                <p className="text-sm"><span className="font-medium">Rounded Corners:</span> {styleConfig.roundedCorners ? 'Yes' : 'No'}</p>
                <p className="text-sm"><span className="font-medium">Include Images:</span> {styleConfig.includeImages ? 'Yes' : 'No'}</p>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium">Colors:</span>
                  {styleConfig.baseColors.map((color, index) => (
                    <div
                      key={index}
                      className="w-8 h-8 rounded"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-medium text-gray-700 mb-2">Final Screen</h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <p className="text-sm"><span className="font-medium">Title:</span> {flow.finalScreen.title}</p>
                <p className="text-sm"><span className="font-medium">Message:</span> {flow.finalScreen.message}</p>
                <p className="text-sm"><span className="font-medium">CTA Text:</span> {flow.finalScreen.ctaText}</p>
                <p className="text-sm"><span className="font-medium">CTA Link:</span> {flow.finalScreen.ctaLink}</p>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t">
            <h3 className="font-medium text-gray-700 mb-4">Preview Links</h3>
            <div className="space-y-2">
              <a
                href={`/${flow.flowName}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-blue-600 hover:text-blue-800"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                View Live Flow
              </a>
              <p className="text-sm text-gray-500">
                Share this link: <code className="bg-gray-100 px-2 py-1 rounded">{window.location.origin}/{flow.flowName}</code>
              </p>
            </div>
          </div>

          <div className="mt-8">
            <h3 className="font-medium text-gray-700 mb-4">Sample Question Preview</h3>
            <div 
              className={`border-2 p-6 ${styleConfig.roundedCorners ? 'rounded-lg' : ''}`}
              style={{ 
                borderColor: styleConfig.baseColors[0],
                fontFamily: styleConfig.fontType
              }}
            >
              <h4 className={`font-semibold mb-4 ${fontSize}`} style={{ color: styleConfig.baseColors[0] }}>
                What is your main fitness goal?
              </h4>
              <div className="space-y-2">
                {['Lose weight', 'Build muscle', 'Improve endurance', 'General health'].map((option, index) => (
                  <label
                    key={index}
                    className={`flex items-center p-3 border ${styleConfig.roundedCorners ? 'rounded-md' : ''} cursor-pointer hover:bg-gray-50`}
                    style={{ borderColor: styleConfig.baseColors[1] }}
                  >
                    <input
                      type="radio"
                      name="sample"
                      className="mr-3"
                      style={{ accentColor: styleConfig.baseColors[0] }}
                    />
                    <span className={fontSize}>{option}</span>
                  </label>
                ))}
              </div>
              <div className="mt-6 flex justify-between">
                <button
                  className={`px-4 py-2 border ${styleConfig.roundedCorners ? 'rounded-md' : ''} ${fontSize}`}
                  style={{ borderColor: styleConfig.baseColors[0], color: styleConfig.baseColors[0] }}
                >
                  Back
                </button>
                <button
                  className={`px-4 py-2 text-white ${styleConfig.roundedCorners ? 'rounded-md' : ''} ${fontSize}`}
                  style={{ backgroundColor: styleConfig.baseColors[0] }}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default FlowPreview;
